import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Progress,
  ScrollArea,
  MultiSelect,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconClock,
  IconCloudDownload,
  IconDatabase,
  IconRefresh,
  IconX,
} from '@tabler/icons-react';
import { User } from 'firebase/auth';
import {
  ExternalImportRun,
  ExternalSource,
  cancelExternalImportRun,
  fetchExternalImportRuns,
  fetchExternalSources,
  startExternalImport,
} from '../../services/adminExternalImportService';
import { doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useFirestoreTrigger } from '../../hooks/useFirestoreTrigger';

const STATUS_COLORS: Record<ExternalImportRun['status'], string> = {
  running: 'blue',
  cancel_requested: 'orange',
  cancelled: 'gray',
  completed: 'green',
  failed: 'red',
};

const STATUS_LABELS: Record<ExternalImportRun['status'], string> = {
  running: 'running',
  cancel_requested: 'cancelling',
  cancelled: 'cancelled',
  completed: 'completed',
  failed: 'failed',
};

const skippedTotal = (run: ExternalImportRun) =>
  run.skippedExisting +
  run.skippedRejected +
  run.skippedDuplicate +
  run.skippedCategory +
  run.skippedNotAMeal +
  run.skippedIncomplete +
  run.skippedLowConfidence;

const isActive = (run: ExternalImportRun) =>
  run.status === 'running' || run.status === 'cancel_requested';

// The sources' normalized course vocabulary. Mains + soups is the historical
// dinner default; breakfast landed with slot support. There is no "lunch"
// label at either source.
const COURSE_OPTIONS = [
  { value: 'main_dish', label: 'Main dish' },
  { value: 'soup', label: 'Soup' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'salad', label: 'Salad' },
  { value: 'side_dish', label: 'Side dish' },
  { value: 'sauce', label: 'Sauce' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'bread', label: 'Bread' },
  { value: 'snack', label: 'Snack' },
  { value: 'beverage', label: 'Beverage' },
];

export const ExternalSourcesPanel = ({ user }: { user: User }) => {
  const [sources, setSources] = useState<ExternalSource[]>([]);
  const [runs, setRuns] = useState<ExternalImportRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [mode, setMode] = useState<'sample' | 'full'>('sample');
  const [sampleSize, setSampleSize] = useState<number | ''>(25);
  const [imageStrategy, setImageStrategy] = useState<string | null>('source');
  const [courses, setCourses] = useState<string[]>(['main_dish', 'soup']);
  const [includeRejected, setIncludeRejected] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      setRuns(await fetchExternalImportRuns(user));
    } catch {
      // Silent refresh failure; the next poll retries.
    } finally {
      setRunsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchExternalSources(user)
      .then((fetched) => {
        setSources(fetched);
        setSelectedSource((current) => current ?? fetched[0]?.id ?? null);
      })
      .catch(() => {});
    loadRuns();
  }, [user, loadRuns]);

  const activeRun = runs.find(isActive);
  const activeRunId = activeRun?.id;

  // Live-refresh while a run is in flight: watch the active run's Firestore
  // doc (admins have read access via security rules) and reload the runs list
  // on every progress write. Falls back to 5s polling if the listener fails.
  const activeRunDocRef = useMemo(
    () => (activeRunId ? doc(db, 'external_import_runs', activeRunId) : null),
    [activeRunId]
  );
  useFirestoreTrigger({
    enabled: !!activeRunId,
    target: activeRunDocRef,
    fallbackIntervalMs: 5000,
    onTrigger: () => loadRuns(),
  });

  const sourceName = (id: string) =>
    sources.find((source) => source.id === id)?.name ?? id;

  const runImport = async () => {
    if (!selectedSource) return;
    setStarting(true);
    try {
      const { runId, total } = await startExternalImport(user, {
        source: selectedSource,
        mode,
        sampleSize: mode === 'sample' && sampleSize ? sampleSize : undefined,
        imageStrategy: (imageStrategy ?? 'source') as
          | 'source'
          | 'generate'
          | 'none',
        categories: courses.length > 0 ? courses : undefined,
        includeRejected,
      });
      notifications.show({
        title: 'Import started',
        message: `Run ${runId} covers ${total} recipes. Imported recipes land in the seed queue for review.`,
        color: 'green',
      });
      loadRuns();
    } catch (error: any) {
      notifications.show({
        title: 'Import failed to start',
        message: error.message,
        color: 'red',
      });
    } finally {
      setStarting(false);
    }
  };

  const handleStart = () => {
    if (mode === 'full') {
      modals.openConfirmModal({
        title: 'Start full import?',
        children: (
          <Text size="sm">
            This walks every recipe {sourceName(selectedSource ?? '')} publishes
            and can run for a few hours. Recipes already imported are skipped,
            so re-running is safe and picks up only new recipes.
          </Text>
        ),
        labels: { confirm: 'Start full import', cancel: 'Cancel' },
        onConfirm: runImport,
      });
    } else {
      runImport();
    }
  };

  const handleCancel = (runId: string) => {
    modals.openConfirmModal({
      title: 'Cancel this import run?',
      children: (
        <Text size="sm">
          The run stops after the recipe currently being imported. Recipes
          already queued stay in the seed queue.
        </Text>
      ),
      labels: { confirm: 'Cancel run', cancel: 'Keep running' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setCancelling(true);
        try {
          await cancelExternalImportRun(user, runId);
          loadRuns();
        } catch (error: any) {
          notifications.show({
            title: 'Cancel failed',
            message: error.message,
            color: 'red',
          });
        } finally {
          setCancelling(false);
        }
      },
    });
  };

  return (
    <Paper withBorder p="md" radius="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <div>
            <Title order={3}>External Recipe Sources</Title>
            <Text size="sm" c="dimmed">
              Import third-party recipes into the seed queue for review
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size="1rem" />}
            variant="outline"
            onClick={loadRuns}
            loading={runsLoading}
          >
            Refresh
          </Button>
        </Group>

        <Group align="flex-end" gap="sm">
          <Select
            label="Source"
            value={selectedSource}
            onChange={setSelectedSource}
            data={sources.map((source) => ({
              value: source.id,
              label: source.name,
            }))}
            style={{ flex: 1, minWidth: 220 }}
          />
          <div>
            <Text size="sm" fw={500} mb={4}>
              Scope
            </Text>
            <SegmentedControl
              value={mode}
              onChange={(value) => setMode(value as 'sample' | 'full')}
              data={[
                { value: 'sample', label: 'Sample' },
                { value: 'full', label: 'Full catalog' },
              ]}
            />
          </div>
          {mode === 'sample' && (
            <NumberInput
              label="Sample size"
              value={sampleSize}
              onChange={(value) =>
                setSampleSize(value === '' ? '' : Number(value))
              }
              min={1}
              max={200}
              w={110}
            />
          )}
          <Select
            label="Images"
            value={imageStrategy}
            onChange={setImageStrategy}
            data={[
              { value: 'source', label: 'Source photo' },
              { value: 'generate', label: 'Generate' },
              { value: 'none', label: 'None' },
            ]}
            w={140}
          />
          <MultiSelect
            label="Courses"
            value={courses}
            onChange={setCourses}
            data={COURSE_OPTIONS}
            w={240}
            clearable={false}
          />
          <Tooltip
            withArrow
            label="Re-import recipes previously rejected from the seed queue. Use when the product scope broadens."
            multiline
            w={260}
            styles={{ tooltip: { fontSize: '0.75rem', lineHeight: 1.4 } }}
          >
            <Checkbox
              label="Include rejected"
              checked={includeRejected}
              onChange={(e) => setIncludeRejected(e.currentTarget.checked)}
              mb={8}
            />
          </Tooltip>
          <Button
            leftSection={<IconCloudDownload size="1rem" />}
            onClick={handleStart}
            loading={starting}
            disabled={!selectedSource || !!activeRun}
          >
            Start Import
          </Button>
        </Group>

        {activeRun && (
          <Card withBorder radius="lg" padding="md">
            <Stack gap="xs">
              <Group justify="space-between" align="center">
                <Group gap="xs">
                  <Text fw={600}>{sourceName(activeRun.sourceId)}</Text>
                  <Badge
                    color={STATUS_COLORS[activeRun.status]}
                    variant="light"
                    leftSection={<IconClock size="0.8rem" />}
                  >
                    {STATUS_LABELS[activeRun.status]}
                  </Badge>
                </Group>
                <Button
                  size="xs"
                  color="red"
                  variant="outline"
                  onClick={() => handleCancel(activeRun.id)}
                  loading={cancelling}
                  disabled={activeRun.status === 'cancel_requested'}
                >
                  Cancel run
                </Button>
              </Group>
              <Progress
                value={(activeRun.processed / Math.max(1, activeRun.total)) * 100}
                animated
              />
              <Text size="sm" c="dimmed">
                {activeRun.processed} / {activeRun.total} processed
                {' · '}
                {activeRun.queued} queued for review
                {' · '}
                {skippedTotal(activeRun)} skipped
                {' · '}
                {activeRun.failed} failed
              </Text>
            </Stack>
          </Card>
        )}

        {runsLoading && runs.length === 0 ? (
          <Stack gap="xs">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={40} radius="sm" />
            ))}
          </Stack>
        ) : runs.length === 0 ? (
          <Alert
            icon={<IconDatabase size="1rem" />}
            title="No Import Runs"
            color="blue"
            radius="lg"
          >
            No imports have been run yet.
          </Alert>
        ) : (
          <ScrollArea>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Progress</Table.Th>
                  <Table.Th>Queued</Table.Th>
                  <Table.Th>Skipped</Table.Th>
                  <Table.Th>Failed</Table.Th>
                  <Table.Th>Started</Table.Th>
                  <Table.Th>Completed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {runs.map((run) => (
                  <Table.Tr key={run.id}>
                    <Table.Td>
                      <Text fw={500} size="sm">
                        {sourceName(run.sourceId)}
                      </Text>
                      {run.dryRun && (
                        <Badge size="xs" variant="light" color="gray">
                          Dry Run
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={STATUS_COLORS[run.status]}
                        variant="light"
                        leftSection={
                          run.status === 'completed' ? (
                            <IconCheck size="0.8rem" />
                          ) : run.status === 'failed' ? (
                            <IconX size="0.8rem" />
                          ) : (
                            <IconClock size="0.8rem" />
                          )
                        }
                      >
                        {STATUS_LABELS[run.status]}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {run.processed} / {run.total}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{run.queued}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip
                        withArrow
                        label={`${run.skippedExisting} already imported, ${run.skippedRejected} previously rejected, ${run.skippedDuplicate} near-duplicates, ${run.skippedCategory} out-of-scope category, ${run.skippedNotAMeal} below calorie floor, ${run.skippedIncomplete} incomplete, ${run.skippedLowConfidence} low confidence`}
                        multiline
                        w={280}
                        styles={{ tooltip: { fontSize: '0.75rem', lineHeight: 1.4 } }}
                      >
                        <Text size="sm">{skippedTotal(run)}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td>
                      {run.failed > 0 ? (
                        <Tooltip
                          withArrow
                          label={(run.failures ?? [])
                            .slice(0, 5)
                            .map((failure) => failure.externalId)
                            .join(', ')}
                          multiline
                          w={260}
                          styles={{ tooltip: { fontSize: '0.75rem', lineHeight: 1.4 } }}
                        >
                          <Text size="sm" c="red">
                            {run.failed}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text size="sm">0</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {new Date(run.startedAt).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {run.completedAt
                          ? new Date(run.completedAt).toLocaleString()
                          : '-'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>
    </Paper>
  );
};
